import { v4 as uuidv4 } from "uuid";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import db from "./FirestoreConfig";

const BLOCK_ROOT_KNOWN_FIELDS = [
  "id",
  "name",
  "notes",
  "weeks",
  "total_weeks",
  "label",
  "workouts",
  "protocols",
  "hidden_weeks",
  "week_names",
  "modified_by",
  "last_update",
  "calendar_start_date",
  "order",
  "_rev",
  "updated_at",
  "op_id",
];

const WORKOUT_KNOWN_FIELDS = [
  "id",
  "date",
  "name",
  "order",
  "week",
  "exercises",
  "last_update",
  "notes",
  "device_os",
  "_rev",
  "updated_at",
  "op_id",
];

const EXERCISE_KNOWN_FIELDS = [
  "id",
  "name",
  "notes",
  "order",
  "original_name",
  "series",
  "attributes",
  "is_secondary_protocol",
  "protocol_slot",
  "protocol_text",
  "protocol_week",
  "link",
  "_rev",
  "updated_at",
  "op_id",
];

const SERIE_KNOWN_FIELDS = [
  "id",
  "order",
  "reps",
  "rpe",
  "weight",
  "target_reps",
  "target_rpe",
  "target_weight",
  "relevance",
  "has_video",
  "require_video",
];

const ATTRIBUTE_KNOWN_FIELDS = ["id", "name", "title", "order", "subtitle"];

const normalizeId = (value) => String(value ?? "").trim().toLowerCase();

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const toArray = (value) => (Array.isArray(value) ? value : []);

const getSafeRev = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const mergeKnownObject = (remote = {}, local = {}, knownFields = []) => {
  const merged = { ...(remote || {}) };
  const knownSet = new Set(knownFields);

  knownFields.forEach((field) => {
    if (hasOwn(local, field)) {
      merged[field] = local[field];
    } else {
      delete merged[field];
    }
  });

  Object.keys(local || {}).forEach((field) => {
    if (!knownSet.has(field)) {
      merged[field] = local[field];
    }
  });

  return merged;
};

const mergePatchFields = (remote = {}, patch = {}) => ({
  ...(remote || {}),
  ...(patch || {}),
});

const findByNormalizedId = (items, id) =>
  toArray(items).find((item) => normalizeId(item?.id) === normalizeId(id));

const mergeKnownArrayByLocalOrder = (remoteItems, localItems, knownFields) =>
  toArray(localItems).map((localItem) => {
    const remoteItem = findByNormalizedId(remoteItems, localItem?.id);
    return mergeKnownObject(remoteItem, localItem, knownFields);
  });

const mergeExerciseRecord = ({
  remoteExercise,
  localExercise,
  metadata = null,
}) => {
  const mergedExercise = mergeKnownObject(
    remoteExercise,
    localExercise,
    EXERCISE_KNOWN_FIELDS,
  );

  mergedExercise.series = mergeKnownArrayByLocalOrder(
    remoteExercise?.series,
    localExercise?.series,
    SERIE_KNOWN_FIELDS,
  );
  mergedExercise.attributes = mergeKnownArrayByLocalOrder(
    remoteExercise?.attributes,
    localExercise?.attributes,
    ATTRIBUTE_KNOWN_FIELDS,
  );

  if (metadata) {
    mergedExercise._rev = metadata.nextRev;
    mergedExercise.updated_at = metadata.nestedUpdatedAt;
    mergedExercise.op_id = metadata.opId;
  }

  return mergedExercise;
};

const mergeWorkoutRecord = ({
  remoteWorkout,
  localWorkout,
  metadata,
}) => {
  const mergedWorkout = mergeKnownObject(
    remoteWorkout,
    localWorkout,
    WORKOUT_KNOWN_FIELDS,
  );

  mergedWorkout.exercises = toArray(localWorkout?.exercises).map((localExercise) => {
    const remoteExercise = findByNormalizedId(
      remoteWorkout?.exercises,
      localExercise?.id,
    );
    return mergeExerciseRecord({
      remoteExercise,
      localExercise,
      metadata,
    });
  });

  mergedWorkout._rev = metadata.nextRev;
  mergedWorkout.updated_at = metadata.nestedUpdatedAt;
  mergedWorkout.last_update = metadata.lastUpdate;
  mergedWorkout.op_id = metadata.opId;

  return mergedWorkout;
};

const mergeProtocolRecords = (remoteProtocols, localProtocols) =>
  toArray(localProtocols).map((localProtocol) => {
    const remoteProtocol = findByNormalizedId(remoteProtocols, localProtocol?.id);
    return mergeExerciseRecord({
      remoteExercise: remoteProtocol,
      localExercise: localProtocol,
    });
  });

export const getProgramBlockNextRev = (remoteBlockData) =>
  getSafeRev(remoteBlockData?._rev) + 1;

export const resolveTouchedWorkoutIds = ({
  remoteBlockData,
  localBlockData,
  touchedWorkoutIds = [],
  allWorkoutsTouched = false,
}) => {
  const localWorkouts = toArray(localBlockData?.workouts);
  if (allWorkoutsTouched || !remoteBlockData) {
    return new Set(localWorkouts.map((workout) => normalizeId(workout?.id)));
  }

  return new Set(toArray(touchedWorkoutIds).map(normalizeId).filter(Boolean));
};

export const buildProgramBlockWriteData = ({
  remoteBlockData = null,
  localBlockData = null,
  rootPatch = null,
  rootFieldDeletes = [],
  deletedWorkoutIds = [],
  touchedWorkoutIds = [],
  allWorkoutsTouched = false,
  replaceNestedArrays = false,
  timestamp,
  rootUpdatedAt = null,
  opId,
} = {}) => {
  const nextRev = getProgramBlockNextRev(remoteBlockData);
  const lastUpdate = timestamp ?? new Date();
  const metadata = {
    nextRev,
    opId: opId || uuidv4(),
    lastUpdate,
    // Firestore serverTimestamp sentinels are not safe inside array values.
    nestedUpdatedAt: lastUpdate,
  };

  let mergedBlock = localBlockData
    ? mergeKnownObject(remoteBlockData, localBlockData, BLOCK_ROOT_KNOWN_FIELDS)
    : mergePatchFields(remoteBlockData, rootPatch);

  if (localBlockData?.protocols) {
    mergedBlock.protocols = mergeProtocolRecords(
      remoteBlockData?.protocols,
      localBlockData.protocols,
    );
  }

  const deletedWorkoutIdSet = new Set(
    toArray(deletedWorkoutIds).map(normalizeId).filter(Boolean),
  );
  const touchedWorkoutIdSet = resolveTouchedWorkoutIds({
    remoteBlockData,
    localBlockData,
    touchedWorkoutIds,
    allWorkoutsTouched,
  });
  const localWorkoutById = new Map(
    toArray(localBlockData?.workouts).map((workout) => [
      normalizeId(workout?.id),
      workout,
    ]),
  );

  if (localBlockData) {
    const remoteWorkouts = toArray(remoteBlockData?.workouts);
    const mergedWorkouts = [];
    const processedWorkoutIds = new Set();

    if (replaceNestedArrays) {
      toArray(localBlockData.workouts).forEach((localWorkout) => {
        const workoutId = normalizeId(localWorkout?.id);
        if (deletedWorkoutIdSet.has(workoutId)) {
          return;
        }

        const remoteWorkout = findByNormalizedId(remoteWorkouts, workoutId);
        mergedWorkouts.push(
          mergeWorkoutRecord({
            remoteWorkout,
            localWorkout,
            metadata,
          }),
        );
      });

      mergedBlock.workouts = mergedWorkouts;
      rootFieldDeletes.forEach((field) => {
        delete mergedBlock[field];
      });

      mergedBlock._rev = nextRev;
      mergedBlock.op_id = metadata.opId;
      mergedBlock.updated_at = rootUpdatedAt || lastUpdate;
      mergedBlock.last_update = lastUpdate;

      return mergedBlock;
    }

    remoteWorkouts.forEach((remoteWorkout) => {
      const workoutId = normalizeId(remoteWorkout?.id);
      if (deletedWorkoutIdSet.has(workoutId)) {
        return;
      }

      const localWorkout = localWorkoutById.get(workoutId);
      if (localWorkout && touchedWorkoutIdSet.has(workoutId)) {
        mergedWorkouts.push(
          mergeWorkoutRecord({
            remoteWorkout,
            localWorkout,
            metadata,
          }),
        );
      } else {
        mergedWorkouts.push(remoteWorkout);
      }
      processedWorkoutIds.add(workoutId);
    });

    toArray(localBlockData.workouts).forEach((localWorkout) => {
      const workoutId = normalizeId(localWorkout?.id);
      if (
        !processedWorkoutIds.has(workoutId) &&
        !deletedWorkoutIdSet.has(workoutId) &&
        touchedWorkoutIdSet.has(workoutId)
      ) {
        mergedWorkouts.push(
          mergeWorkoutRecord({
            remoteWorkout: null,
            localWorkout,
            metadata,
          }),
        );
      }
    });

    mergedBlock.workouts = mergedWorkouts;
  }

  rootFieldDeletes.forEach((field) => {
    delete mergedBlock[field];
  });

  mergedBlock._rev = nextRev;
  mergedBlock.op_id = metadata.opId;
  mergedBlock.updated_at = rootUpdatedAt || lastUpdate;
  mergedBlock.last_update = lastUpdate;

  return mergedBlock;
};

const resolveDocRef = (firestore, operation) => {
  if (operation.docRef) {
    return operation.docRef;
  }
  if (Array.isArray(operation.pathSegments) && operation.pathSegments.length > 0) {
    return doc(firestore, ...operation.pathSegments);
  }
  throw new Error("program_block_write_missing_doc_ref");
};

export const buildProgramBlockDataFromBlock = ({ block, timestamp }) => {
  const data = block.uploadToCloud(timestamp);
  data.hidden_weeks = Array.isArray(block.hiddenWeeks) ? block.hiddenWeeks : [];
  data.week_names = block.weekNames || {};
  data.protocols = toArray(block.protocols).map((exercise) =>
    exercise.uploadToCloud(),
  );
  return data;
};

export const buildProgramLibraryBlockDataFromBlock = ({ block }) => {
  const data = block.uploadToCloudLibrary();
  data.hidden_weeks = Array.isArray(block.hiddenWeeks) ? block.hiddenWeeks : [];
  data.week_names = block.weekNames || {};
  data.protocols = toArray(block.protocols).map((exercise) =>
    exercise.uploadToCloud(),
  );
  return data;
};

export const getChangedWorkoutIdsFromBlock = (block) =>
  toArray(block?.workouts)
    .filter((workout) => workout?.hasChanged === true)
    .map((workout) => workout.id);

export const commitProgramBlockTransaction = async ({
  operations = [],
  firestore = db,
  timestamp = new Date(),
  opId = uuidv4(),
  rootUpdatedAt = serverTimestamp(),
  context = {},
} = {}) => {
  if (!Array.isArray(operations) || operations.length === 0) {
    return { ok: true, operations: [] };
  }

  return runTransaction(firestore, async (transaction) => {
    const results = [];
    const preparedOperations = [];

    for (const operation of operations) {
      const docRef = resolveDocRef(firestore, operation);
      const snapshot = await transaction.get(docRef);
      const exists =
        typeof snapshot.exists === "function"
          ? snapshot.exists()
          : snapshot.exists === true;
      const remoteBlockData =
        exists && typeof snapshot.data === "function" ? snapshot.data() : null;

      preparedOperations.push({
        operation,
        docRef,
        exists,
        remoteBlockData,
      });
    }

    preparedOperations.forEach(({ operation, exists }) => {
      if (operation.type === "delete" && operation.requireExists && !exists) {
        throw new Error("program_block_delete_source_missing");
      }
      if (operation.type !== "delete" && !exists && operation.createIfMissing === false) {
        throw new Error("program_block_update_source_missing");
      }
    });

    for (const preparedOperation of preparedOperations) {
      const { operation, docRef, exists, remoteBlockData } = preparedOperation;
      if (operation.type === "delete") {
        if (exists || operation.deleteMissing === true) {
          transaction.delete(docRef);
        }
        results.push({
          type: "delete",
          didDelete: exists,
          pathSegments: operation.pathSegments || null,
        });
        continue;
      }

      const localBlockData =
        operation.localBlockData ||
        (operation.block
          ? operation.isLibraryBlock
            ? buildProgramLibraryBlockDataFromBlock({ block: operation.block })
            : buildProgramBlockDataFromBlock({
                block: operation.block,
                timestamp,
              })
          : null);

      const writeData = buildProgramBlockWriteData({
        remoteBlockData,
        localBlockData,
        rootPatch: operation.rootPatch,
        rootFieldDeletes: operation.rootFieldDeletes,
        deletedWorkoutIds: operation.deletedWorkoutIds,
        touchedWorkoutIds: operation.touchedWorkoutIds,
        allWorkoutsTouched: operation.allWorkoutsTouched,
        replaceNestedArrays: operation.replaceNestedArrays,
        timestamp,
        rootUpdatedAt,
        opId,
      });

      transaction.set(docRef, writeData);
      results.push({
        type: operation.type || "upsert",
        nextRev: writeData._rev,
        pathSegments: operation.pathSegments || null,
      });
    }

    return {
      ok: true,
      context,
      operations: results,
    };
  });
};

export const commitProgramBlockWrite = ({
  pathSegments,
  docRef,
  block,
  localBlockData,
  isLibraryBlock = false,
  touchedWorkoutIds,
  deletedWorkoutIds,
  allWorkoutsTouched,
  replaceNestedArrays,
  timestamp,
  context,
  firestore,
} = {}) =>
  commitProgramBlockTransaction({
    firestore,
    timestamp,
    context,
    operations: [
      {
        type: "upsert",
        pathSegments,
        docRef,
        block,
        localBlockData,
        isLibraryBlock,
        touchedWorkoutIds,
        deletedWorkoutIds,
        allWorkoutsTouched,
        replaceNestedArrays,
      },
    ],
  });

export const commitProgramBlockPatch = ({
  pathSegments,
  docRef,
  rootPatch,
  rootFieldDeletes,
  createIfMissing = false,
  timestamp,
  context,
  firestore,
} = {}) =>
  commitProgramBlockTransaction({
    firestore,
    timestamp,
    context,
    operations: [
      {
        type: "patch",
        pathSegments,
        docRef,
        rootPatch,
        rootFieldDeletes,
        createIfMissing,
      },
    ],
  });

export const deleteProgramBlock = ({
  pathSegments,
  docRef,
  requireExists = false,
  timestamp,
  context,
  firestore,
} = {}) =>
  commitProgramBlockTransaction({
    firestore,
    timestamp,
    context,
    operations: [
      {
        type: "delete",
        pathSegments,
        docRef,
        requireExists,
        deleteMissing: false,
      },
    ],
  });
